import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertController, Platform } from '@ionic/angular/standalone';
import { App } from '@capacitor/app';
import { environment } from '../../environments/environment';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppInfoService } from './app-info.service';
import { LoggerService } from './logger.service';

export interface VersionInfo {
  android: {
    minVersion: string;
    latestVersion: string;
  };
  ios: {
    minVersion: string;
    latestVersion: string;
  };
}

export interface AppVersion {
  version: string;
  buildNumber: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService implements OnDestroy {
  private readonly VERSION_ENDPOINTS = {
    dev: 'https://app.dev.piledco.com/version.json',
    staging: 'https://app.stage.piledco.com/version.json',
    prod: 'https://app.us.piledco.com/version.json'
  };

  private appResumeListener: any = null;
  private versionCheckInProgress = false;

  constructor(
    private http: HttpClient,
    private alertController: AlertController,
    private platform: Platform,
    private appInfoService: AppInfoService,
    private logger: LoggerService
  ) {
    this.setupAppResumeListener();
  }

  /**
   * Setup app resume listener to check version when app comes back from background
   */
  private setupAppResumeListener(): void {
    if (this.platform.is('capacitor')) {
      this.logger.log('Setting up app resume listener for version checks');
      this.appResumeListener = App.addListener('resume', () => {
        this.logger.log('App resumed - checking version support');
        this.checkVersionSupport();
      });
    } else {
      this.logger.log('App resume listener not set up - running on web browser');
    }
  }

  /**
   * Cleanup app resume listener
   */
  ngOnDestroy(): void {
    if (this.appResumeListener) {
      this.logger.log('Cleaning up app resume listener');
      this.appResumeListener.remove();
      this.appResumeListener = null;
    }
  }

  /**
   * Check if the current app version is supported
   * Only runs on tablet devices (not web browsers)
   */
  async checkVersionSupport(): Promise<void> {
    this.logger.log('Version check initiated at:', new Date().toISOString());
    
    // Prevent multiple simultaneous version checks
    if (this.versionCheckInProgress) {
      this.logger.log('Version check already in progress, skipping');
      return;
    }

    this.versionCheckInProgress = true;
    this.logger.log('Version check started - setting inProgress flag');

    try {
      // First, check if user is on mobile phone accessing the S3 website
      // This should block everything if true
      // **** Alec's changes to get this working ****
      //Removing this for now to get the app working
      /*
      if (this.isMobilePhone()) {
        this.logger.log('Mobile phone detected - blocking access to S3 website');
        await this.showMobilePhoneAlert();
        return; // Block all further execution
      }
      */

      // Only check version on tablet devices (not web browsers)
      if (!this.isTabletDevice()) {
        this.logger.log('Version check skipped - running on web browser');
        return;
      }

      this.logger.log('Proceeding with version check on tablet device');
      const currentVersion = await this.getCurrentAppVersion();
      const versionInfo = await this.getVersionInfo().toPromise();
      
      this.logger.log('Version check completed:', {
        currentVersion: currentVersion.version,
        minVersion: versionInfo?.android?.minVersion,
        latestVersion: versionInfo?.android?.latestVersion,
        isSupported: versionInfo ? this.isVersionSupported(currentVersion.version, versionInfo) : true,
        timestamp: new Date().toISOString()
      });
      
      if (versionInfo && !this.isVersionSupported(currentVersion.version, versionInfo)) {
        this.logger.log('Version not supported - showing alert');
        await this.showVersionAlert(currentVersion.version, versionInfo);
      } else {
        this.logger.log('Version check passed - app is supported');
      }
    } catch (error) {
      this.logger.error('Version check failed:', error);
      // Don't block the app if version check fails
    } finally {
      this.versionCheckInProgress = false;
      this.logger.log('Version check completed - cleared inProgress flag');
    }
  }

  /**
   * Check if user is on mobile phone accessing the S3 website
   * This should be called early in the app lifecycle to prevent mobile phone usage
   */
  async checkMobilePhoneAccess(): Promise<void> {
    // Only check on web browsers (S3 website), not on native apps
    if (this.platform.is('capacitor')) {
      return;
    }

    // Check if this is a mobile phone (not tablet)
    if (this.isMobilePhone()) {
      await this.showMobilePhoneAlert();
    }
  }

  /**
   * Determine if the app is running on a tablet device vs web browser
   */
  private isTabletDevice(): boolean {
    // Check if running on Capacitor (tablet/phone) vs web browser
    return this.platform.is('capacitor') || this.platform.is('mobile');
  }

  /**
   * Determine if the user is on a mobile phone (not tablet) accessing the website
   * This is much simpler now - we just need to distinguish:
   * 1. Capacitor app (bundled Android app) - always allow
   * 2. Web browser on phone - block
   * 3. Web browser on tablet/desktop - allow
   */
  private isMobilePhone(): boolean {
    // If running on Capacitor (bundled Android app), it's never a "mobile phone" in our context
    if (this.platform.is('capacitor')) {
      return false;
    }

    // We're on a web browser (S3 website)
    // Now just check if the viewport is small enough to be a phone
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minDimension = Math.min(viewportWidth, viewportHeight);
    
    // Consider it a mobile phone if viewport width is less than 768px
    // This is a standard breakpoint for mobile vs tablet
    const isMobilePhone = minDimension < 768;
    
    this.logger.log('Mobile phone detection:', {
      platform: 'web-browser',
      viewportDimensions: `${viewportWidth}x${viewportHeight}`,
      minDimension,
      isMobilePhone
    });
    
    return isMobilePhone;
  }

  /**
   * Show mobile phone alert - no way to dismiss since user can't use the system
   */
  private async showMobilePhoneAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Device Not Supported',
      message: 'Piled is an in-field complex project management system not suitable to be used on phones. Please use a computer or a Piled tablet to continue.',
      buttons: [], // No buttons - user cannot dismiss or proceed
      backdropDismiss: false,
      cssClass: 'mobile-phone-alert-large',
      mode: 'ios' // Use iOS mode for better styling options
    });

    await alert.present();
  }

  /**
   * Get the current app version from the device
   */
  private async getCurrentAppVersion(): Promise<AppVersion> {
    const appInfo = await this.appInfoService.getAppInfo();
    return {
      version: appInfo.version,
      buildNumber: appInfo.buildNumber
    };
  }

  /**
   * Get version information from the server
   */
  private getVersionInfo(): Observable<VersionInfo | null> {
    const endpoint = this.getVersionEndpoint();
    return this.http.get<VersionInfo>(endpoint).pipe(
      catchError(error => {
        this.logger.error('Failed to fetch version info:', error);
        return of(null);
      })
    );
  }

  /**
   * Determine which version endpoint to use based on environment
   */
  private getVersionEndpoint(): string {
    switch (environment.environmentName) {
      case 'staging':
        return this.VERSION_ENDPOINTS.staging;
      case 'production':
        return this.VERSION_ENDPOINTS.prod;
      case 'development':
      case 'local':
      default:
        return this.VERSION_ENDPOINTS.dev;
    }
  }

  /**
   * Check if the current version is supported
   */
  private isVersionSupported(currentVersion: string, versionInfo: VersionInfo): boolean {
    const minVersion = versionInfo.android.minVersion;
    return this.compareVersions(currentVersion, minVersion) >= 0;
  }

  /**
   * Compare two version strings
   * Returns: -1 if v1 < v2, 0 if v1 == v2, 1 if v1 > v2
   */
  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    
    const maxLength = Math.max(parts1.length, parts2.length);
    
    for (let i = 0; i < maxLength; i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;
      
      if (part1 < part2) return -1;
      if (part1 > part2) return 1;
    }
    
    return 0;
  }

  /**
   * Show version alert to user
   */
  private async showVersionAlert(currentVersion: string, versionInfo: VersionInfo): Promise<void> {
    const alert = await this.alertController.create({
      header: 'App Update Required',
      message: `
        Your app version (${currentVersion}) is no longer supported.\n
        The minimum supported version is ${versionInfo.android.minVersion}.\n
        The latest available version is ${versionInfo.android.latestVersion}.
        Please open the play store and update the Piled app.\n
        If you need assistance, please contact support at 1-888-72-PILED.
      `,
      buttons: [
        {
          text: 'Close App',
          handler: async () => {
            await this.closeApp();
          }
        }
      ],
      backdropDismiss: false,
      cssClass: 'version-alert'
    });

    await alert.present();
  }

  /**
   * Restart the device
   */
  private async restartDevice(): Promise<void> {
    try {
      if (this.platform.is('capacitor')) {
        // On native devices, restart the app
        this.logger.log('Restarting app...');
        await App.exitApp();
        // The app will be restarted by the system or user
      } else {
        // On web browsers, reload the page
        this.logger.log('Reloading web page...');
        window.location.reload();
      }
    } catch (error) {
      this.logger.error('Failed to restart device:', error);
      // Fallback: show a message to the user
      const alert = await this.alertController.create({
        header: 'Restart Required',
        message: 'Please manually restart the app to apply updates.',
        buttons: ['OK']
      });
      await alert.present();
    }
  }

  /**
   * Download the latest version
   */
  private downloadLatestVersion(): void {
    try {
      if (this.platform.is('android')) {
        // Redirect to Google Play Store
        const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.piledtech.app';
        window.open(playStoreUrl, '_system');
      } else if (this.platform.is('ios')) {
        // Redirect to Apple App Store
        const appStoreUrl = 'https://apps.apple.com/app/piled/id123456789'; // Replace with actual App Store ID
        window.open(appStoreUrl, '_system');
      } else {
        // For web browsers, show a message
        this.showDownloadMessage();
      }
    } catch (error) {
      this.logger.error('Failed to open app store:', error);
      this.showDownloadMessage();
    }
  }

  /**
   * Show download message for web browsers
   */
  private async showDownloadMessage(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Download Latest Version',
      message: 'Please visit your device\'s app store to download the latest version of the Piled app. If you need assistance, please contact support at 1-888-72-PILED.',
      buttons: ['OK']
    });
    await alert.present();
  }

  /**
   * Close the app with fallback force close mechanism
   */
  private async closeApp(): Promise<void> {
    this.logger.log('closeApp() called at:', new Date().toISOString());
    
    try {
      if (this.platform.is('capacitor')) {
        this.logger.log('Attempting to close app on Capacitor platform...');
        await App.exitApp();
        this.logger.log('App.exitApp() completed successfully');
        
        // If we reach here, exitApp might not have worked completely
        // Add a fallback force close after a short delay
        this.logger.log('Setting up fallback force close in 2 seconds...');
        setTimeout(async () => {
          this.logger.log('Fallback force close triggered at:', new Date().toISOString());
          try {
            await App.exitApp();
            this.logger.log('Fallback force close completed successfully');
          } catch (fallbackError) {
            this.logger.error('Fallback force close also failed:', fallbackError);
            // Show user a message that they need to manually close the app
            await this.showManualCloseMessage();
          }
        }, 2000);
      } else {
        // For web browsers, just reload the page to simulate closing
        this.logger.log('Reloading web page to simulate app close');
        window.location.reload();
      }
    } catch (error) {
      this.logger.error('Failed to close app:', error);
      
      // Force close even if there's an error
      if (this.platform.is('capacitor')) {
        this.logger.log('Attempting force close after error...');
        try {
          await App.exitApp();
          this.logger.log('Force close after error completed successfully');
        } catch (forceCloseError) {
          this.logger.error('Force close also failed:', forceCloseError);
          // Show user a message that they need to manually close the app
          await this.showManualCloseMessage();
        }
      }
    }
  }

  /**
   * Show message to user that they need to manually close the app
   */
  private async showManualCloseMessage(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'App Close Required',
      message: 'Please manually close the Piled app from your device\'s recent apps or task manager. The app version is no longer supported and must be updated.',
      buttons: [
        {
          text: 'Try Again',
          handler: async () => {
            await this.closeApp();
          }
        },
        {
          text: 'OK',
          role: 'cancel'
        }
      ],
      backdropDismiss: false,
      cssClass: 'version-alert'
    });
    await alert.present();
  }

  /**
   * Get current environment info for debugging
   */
  getEnvironmentInfo(): any {
    return {
      isTablet: this.isTabletDevice(),
      isMobilePhone: this.isMobilePhone(),
      isProduction: environment.production,
      versionEndpoint: this.getVersionEndpoint(),
      platform: this.platform.platforms(),
      deviceInfo: this.getDeviceInfo()
    };
  }

  /**
   * Get detailed device information for debugging
   */
  getDeviceInfo(): any {
    if (this.platform.is('capacitor')) {
      return {
        platform: 'capacitor',
        type: 'native-app'
      };
    }

    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const pixelRatio = window.devicePixelRatio || 1;
    
    return {
      platform: 'web-browser',
      userAgent: userAgent,
      screenDimensions: `${screenWidth}x${screenHeight}`,
      viewportDimensions: `${viewportWidth}x${viewportHeight}`,
      pixelRatio,
      isMobilePhone: this.isMobilePhone(),
      isTablet: this.isTabletDevice()
    };
  }
} 